import React, { useState } from 'react';
import { toast } from 'react-toastify';

function DataAnalysis() {
  const [activeTab, setActiveTab] = useState('upload');
  const [datasets, setDatasets] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleDataUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsUploading(true);
    const formData = new FormData();
    formData.append('data', file);
    formData.append('dataType', 'prüfwerte');
    formData.append('equipmentType', 'röntgen');
    formData.append('companyId', '1'); // Später dynamisch

    try {
      const response = await fetch('/api/ki-workspace/datenanalyse/prüfwerte/upload', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Datensatz "${data.dataset.filename}" erfolgreich hochgeladen`);
        setDatasets((prev) => [...prev, data.dataset]);
        event.target.value = '';
      } else {
        const error = await response.json();
        toast.error(`Upload-Fehler: ${error.error}`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Fehler beim Upload der Daten');
    } finally {
      setIsUploading(false);
    }
  };

  const extractData = async (datasetId) => {
    setIsProcessing(true);
    try {
      const response = await fetch('/api/ki-workspace/datenanalyse/prüfwerte/extract', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          datasetId,
          extractionRules: {
            fields: ['spannung', 'strom', 'zeit', 'dosis'],
            filters: {
              dateRange: {
                start: '2024-01-01',
                end: '2024-12-31',
              },
            },
          },
        }),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Datenextraktion gestartet: ${data.extraction.id}`);
      } else {
        const error = await response.json();
        toast.error(`Extraktions-Fehler: ${error.error}`);
      }
    } catch (error) {
      console.error('Extraction error:', error);
      toast.error('Fehler bei der Datenextraktion');
    } finally {
      setIsProcessing(false);
    }
  };

  const createPrediction = async (datasetId, predictionType) => {
    setIsProcessing(true);
    try {
      const response = await fetch('/api/ki-workspace/datenanalyse/predictions/create', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          datasetId,
          predictionType,
          timeframe: '3_months',
          equipment: {
            id: 'röntgen_001',
            type: 'röntgen',
            model: 'Siemens Ysio',
          },
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setPredictions((prev) => [...prev, data.prediction]);
        toast.success(
          `Vorhersage erstellt mit ${Math.round(data.prediction.confidence * 100)}% Vertrauen`,
        );
      } else {
        const error = await response.json();
        toast.error(`Vorhersage-Fehler: ${error.error}`);
      }
    } catch (error) {
      console.error('Prediction error:', error);
      toast.error('Fehler bei der Vorhersage-Erstellung');
    } finally {
      setIsProcessing(false);
    }
  };

  const getPredictionTypeColor = (type) => {
    switch (type) {
      case 'maintenance':
        return 'bg-blue-100 text-blue-800';
      case 'failure':
        return 'bg-red-100 text-red-800';
      case 'performance':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPredictionTypeIcon = (type) => {
    switch (type) {
      case 'maintenance':
        return '🔧';
      case 'failure':
        return '⚠️';
      case 'performance':
        return '📈';
      default:
        return '📊';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                  📊 Datenanalyse
                </h1>
                <p className="mt-2 text-gray-600">
                  Prüfwerte-Extraktion und intelligente Vorhersagen
                </p>
              </div>
              <button
                onClick={() => window.history.back()}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-md"
              >
                ← Zurück
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('upload')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'upload'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                📁 Daten hochladen
              </button>
              <button
                onClick={() => setActiveTab('extraction')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'extraction'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                🔍 Extraktion
              </button>
              <button
                onClick={() => setActiveTab('predictions')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'predictions'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                🔮 Vorhersagen
              </button>
            </nav>
          </div>
        </div>

        {/* Upload Tab */}
        {activeTab === 'upload' && (
          <div className="space-y-6">
            {/* Upload Area */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Prüfdaten hochladen</h2>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <div className="text-4xl mb-4">📊</div>
                <p className="text-gray-600 mb-4">
                  CSV, Excel oder JSON-Dateien mit Prüfwerten hochladen
                </p>
                <input
                  type="file"
                  accept=".csv,.xlsx,.json"
                  onChange={handleDataUpload}
                  className="hidden"
                  id="data-upload"
                  disabled={isUploading}
                />
                <label
                  htmlFor="data-upload"
                  className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                    isUploading
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 cursor-pointer'
                  }`}
                >
                  {isUploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Wird hochgeladen...
                    </>
                  ) : (
                    'Datei auswählen'
                  )}
                </label>
                <p className="text-xs text-gray-500 mt-2">
                  Unterstützte Formate: CSV, XLSX, JSON (max. 100MB)
                </p>
              </div>
            </div>

            {/* Datasets List */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Hochgeladene Datensätze</h2>
              </div>
              <div className="p-6">
                {datasets.length > 0 ? (
                  <div className="space-y-3">
                    {datasets.map((dataset) => (
                      <div
                        key={dataset.id}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-md"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="text-2xl">📊</div>
                          <div>
                            <p className="font-medium text-gray-900">{dataset.filename}</p>
                            <p className="text-sm text-gray-500">
                              {dataset.dataType} • {dataset.equipmentType} • {dataset.recordCount}{' '}
                              Datensätze
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              dataset.status === 'processing'
                                ? 'bg-yellow-100 text-yellow-800'
                                : dataset.status === 'completed'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {dataset.status === 'processing'
                              ? 'Verarbeitung'
                              : dataset.status === 'completed'
                                ? 'Fertig'
                                : 'Unbekannt'}
                          </span>
                          <button
                            onClick={() => extractData(dataset.id)}
                            disabled={isProcessing}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs"
                          >
                            Extrahieren
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <div className="text-4xl mb-2">📈</div>
                    <p>Noch keine Datensätze hochgeladen</p>
                    <p className="text-sm">
                      Laden Sie Prüfdaten hoch, um mit der Analyse zu beginnen
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Extraction Tab */}
        {activeTab === 'extraction' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Datenextraktion</h2>
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
                <div className="flex">
                  <div className="text-blue-400 mr-3">ℹ️</div>
                  <div>
                    <h3 className="text-sm font-medium text-blue-800">Extraktion-Information</h3>
                    <p className="text-sm text-blue-700 mt-1">
                      Die KI extrahiert automatisch relevante Prüfwerte aus Ihren Datensätzen.
                      Standardmäßig werden Spannung, Strom, Zeit und Dosis erkannt und strukturiert.
                    </p>
                  </div>
                </div>
              </div>

              {/* Extraction Rules */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-md font-medium text-gray-900 mb-3">Extraktions-Felder</h3>
                  <div className="space-y-2">
                    {['Spannung (kV)', 'Strom (mA)', 'Zeit (ms)', 'Dosis (mGy)'].map(
                      (field, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <input type="checkbox" defaultChecked className="rounded" />
                          <span className="text-sm text-gray-700">{field}</span>
                        </div>
                      ),
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="text-md font-medium text-gray-900 mb-3">Filter-Optionen</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Zeitraum von:</label>
                      <input
                        type="date"
                        defaultValue="2024-01-01"
                        className="w-full border border-gray-300 rounded px-3 py-1 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Zeitraum bis:</label>
                      <input
                        type="date"
                        defaultValue="2024-12-31"
                        className="w-full border border-gray-300 rounded px-3 py-1 text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Extractions */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Letzte Extraktionen</h2>
              </div>
              <div className="p-6">
                <div className="text-center py-8 text-gray-500">
                  <div className="text-4xl mb-2">🔍</div>
                  <p>Noch keine Extraktionen durchgeführt</p>
                  <p className="text-sm">Starten Sie eine Extraktion aus der Datensatz-Liste</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Predictions Tab */}
        {activeTab === 'predictions' && (
          <div className="space-y-6">
            {/* Create Prediction */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Neue Vorhersage erstellen
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button
                  onClick={() =>
                    datasets.length > 0 && createPrediction(datasets[0].id, 'maintenance')
                  }
                  disabled={datasets.length === 0 || isProcessing}
                  className="p-4 border border-gray-200 rounded-lg text-center hover:bg-blue-50 disabled:opacity-50"
                >
                  <div className="text-2xl mb-2">🔧</div>
                  <h3 className="font-medium text-gray-900">Wartung</h3>
                  <p className="text-sm text-gray-600">Wartungsbedarfs-Vorhersage</p>
                </button>

                <button
                  onClick={() => datasets.length > 0 && createPrediction(datasets[0].id, 'failure')}
                  disabled={datasets.length === 0 || isProcessing}
                  className="p-4 border border-gray-200 rounded-lg text-center hover:bg-red-50 disabled:opacity-50"
                >
                  <div className="text-2xl mb-2">⚠️</div>
                  <h3 className="font-medium text-gray-900">Ausfall</h3>
                  <p className="text-sm text-gray-600">Ausfalls-Wahrscheinlichkeit</p>
                </button>

                <button
                  onClick={() =>
                    datasets.length > 0 && createPrediction(datasets[0].id, 'performance')
                  }
                  disabled={datasets.length === 0 || isProcessing}
                  className="p-4 border border-gray-200 rounded-lg text-center hover:bg-green-50 disabled:opacity-50"
                >
                  <div className="text-2xl mb-2">📈</div>
                  <h3 className="font-medium text-gray-900">Performance</h3>
                  <p className="text-sm text-gray-600">Leistungs-Entwicklung</p>
                </button>
              </div>

              {datasets.length === 0 && (
                <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-md p-3">
                  <p className="text-sm text-yellow-800">
                    Laden Sie zuerst Datensätze hoch, um Vorhersagen zu erstellen.
                  </p>
                </div>
              )}
            </div>

            {/* Predictions List */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Erstellte Vorhersagen</h2>
              </div>
              <div className="p-6">
                {predictions.length > 0 ? (
                  <div className="space-y-4">
                    {predictions.map((prediction) => (
                      <div key={prediction.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <div className="text-2xl">{getPredictionTypeIcon(prediction.type)}</div>
                            <div>
                              <h3 className="font-medium text-gray-900 capitalize">
                                {prediction.type}
                              </h3>
                              <p className="text-sm text-gray-500">
                                Vorhersage für {prediction.predictedDate}
                              </p>
                            </div>
                          </div>
                          <span
                            className={`px-3 py-1 rounded-full text-sm font-medium ${getPredictionTypeColor(prediction.type)}`}
                          >
                            {Math.round(prediction.confidence * 100)}% Vertrauen
                          </span>
                        </div>

                        <div className="bg-gray-50 rounded-md p-3 mb-3">
                          <p className="text-sm font-medium text-gray-900 mb-1">Empfehlung:</p>
                          <p className="text-sm text-gray-700">{prediction.recommendation}</p>
                        </div>

                        {prediction.factors && prediction.factors.length > 0 && (
                          <div>
                            <p className="text-sm font-medium text-gray-900 mb-2">
                              Einflussfaktoren:
                            </p>
                            <div className="space-y-2">
                              {prediction.factors.map((factor, index) => (
                                <div
                                  key={index}
                                  className="flex items-center justify-between text-sm"
                                >
                                  <span className="text-gray-600">{factor.factor}:</span>
                                  <div className="flex items-center space-x-2">
                                    <span className="text-gray-900">
                                      {factor.currentValue} / {factor.threshold}
                                    </span>
                                    <div className="w-20 bg-gray-200 rounded-full h-2">
                                      <div
                                        className="bg-blue-600 h-2 rounded-full"
                                        style={{
                                          width: `${Math.min((factor.currentValue / factor.threshold) * 100, 100)}%`,
                                        }}
                                      />
                                    </div>
                                    <span className="text-xs text-gray-500">
                                      {Math.round(factor.impact * 100)}%
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <div className="text-4xl mb-2">🔮</div>
                    <p>Noch keine Vorhersagen erstellt</p>
                    <p className="text-sm">
                      Erstellen Sie Vorhersagen basierend auf Ihren Datensätzen
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DataAnalysis;
